import React, { useState, Component } from 'react';
import { Package, ArrowRight, AlertTriangle, CalendarCheck, ChevronRight, MessageSquare, Clock, CheckCircle2, MapPin } from 'lucide-react';
import { KpiCard } from '../KpiCard';
import { Widget } from '../Widget';
const NEXT_SHIPMENT = {
  id: 'SH-2840-119',
  trackingId: 'FX4892013',
  destination: 'Galleria Vittorio Emanuele, Milano',
  carrier: 'FedEx',
  carrierLogo: 'https://cdn.magicpatterns.com/uploads/bJFTTvq6RiT6tx6MWd19DM/FedEx-Logo.png',
  statusLabel: 'In transito',
  eta: 'Domani · 10:30',
  departure: '09 mag · 08:12',
  progress: 65,
  completedSteps: 3,
  totalSteps: 5,
  currentPhase: 'Hub Milano · 10 mag, 06:20',
  anomaly: {
    summary: 'Orario di consegna fuori dalle aperture del negozio — serve fascia oraria'
  }
};
const CARRIER_LOGOS: Record<string, string> = {
  BRT: 'https://cdn.magicpatterns.com/uploads/cmtJEf49a5eVZLpdSNFSih/BRT_logo_cropped.png',
  FedEx: 'https://cdn.magicpatterns.com/uploads/bJFTTvq6RiT6tx6MWd19DM/FedEx-Logo.png',
  GLS: 'https://cdn.magicpatterns.com/uploads/kLsfczSQhw6HQ17cu2iXe1/GLS_Logo_2021.svg.png',
  SDA: 'https://cdn.magicpatterns.com/uploads/uzuZLxLhuWymtodu3YDZcY/1620120317-sda-poste-logo.webp',
  UPS: 'https://cdn.magicpatterns.com/uploads/5fMcthEgdAwTbqPzFRcjsj/logo_corriere_ups_500.png'
};
const RECENT_SHIPMENTS = [{
  id: 'SH-2840-119',
  name: 'Riassortimento S25',
  carrier: 'FedEx',
  date: '09 mag',
  colli: 6,
  status: 'In transito',
  statusColor: 'text-[#5DA9FF]'
}, {
  id: 'SH-2840-118',
  name: 'Nuova collezione AI25',
  carrier: 'FedEx',
  date: '08 mag',
  colli: 4,
  status: 'In consegna',
  statusColor: 'text-[#D4FF3A]'
}, {
  id: 'SH-2840-117',
  name: 'Riassortimento sneakers',
  carrier: 'GLS',
  date: '07 mag',
  colli: 5,
  status: 'In transito',
  statusColor: 'text-[#5DA9FF]'
}, {
  id: 'SH-2840-116',
  name: 'Stagionale costumi mare',
  carrier: 'UPS',
  date: '06 mag',
  colli: 3,
  status: 'Consegnata',
  statusColor: 'text-[#9A9A9A]'
}];
type TicketStatus = 'Aperto' | 'In lavorazione' | 'Risolto';
const RECENT_TICKETS: {
  id: string;
  subject: string;
  status: TicketStatus;
  updated: string;
}[] = [{
  id: 'TK-9921',
  subject: 'Ritardo nella consegna',
  status: 'In lavorazione',
  updated: '2h fa'
}, {
  id: 'TK-9842',
  subject: 'Richiesta etichetta di reso',
  status: 'Risolto',
  updated: 'Ieri'
}];
const TICKET_STATUS_STYLES: Record<TicketStatus, {
  text: string;
  icon: ComponentType<any>;
}> = {
  Aperto: {
    text: 'text-[#EF4444]',
    icon: AlertTriangle
  },
  'In lavorazione': {
    text: 'text-[#F5A524]',
    icon: Clock
  },
  Risolto: {
    text: 'text-[#9A9A9A]',
    icon: CheckCircle2
  }
};
type DashboardProps = {
  onOpenShipment?: (id: string) => void;
  onOpenTicket?: (id: string) => void;
  onOpenAllTickets?: () => void;
};
export function ClientDashboard({
  onOpenShipment,
  onOpenTicket,
  onOpenAllTickets
}: DashboardProps = {}) {
  const ns = NEXT_SHIPMENT;
  return <div className="space-y-6" data-id="element-88">
      <div data-id="element-89">
        <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight" data-id="element-90">
          Panoramica
        </h1>
      </div>

      {/* KPI strip */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-91">
        <KpiCard label="Spedizioni attive" value="12" data-id="element-92" />
        <KpiCard label="In consegna oggi" value="3" data-id="element-93" />
        <KpiCard label="Resi in corso" value="1" data-id="element-94" />
        <KpiCard label="Ticket aperti" value="1" data-id="element-95" />
      </section>

      {/* Prossima consegna — full width */}
      <Widget title="Prossima consegna" footerCta={{
      label: 'Vedi spedizione',
      onClick: () => onOpenShipment?.(ns.id)
    }} bodyPadding="none" data-id="element-96">
        {/* Identity + KPIs row */}
        <div className="p-4 sm:p-5 flex items-start justify-between gap-6 flex-wrap" data-id="element-97">
          <div className="flex items-center gap-4 min-w-0" data-id="element-98">
            <div className="h-14 w-16 rounded-lg bg-white flex items-center justify-center shrink-0 p-2" data-id="element-99">
              <img src={ns.carrierLogo} alt={ns.carrier} className="max-w-full max-h-full object-contain" data-id="element-100" />
            </div>
            <div className="min-w-0" data-id="element-101">
              <div className="flex items-center gap-2 mb-1" data-id="element-102">
                <Package className="h-3.5 w-3.5 text-[#6B6B6B]" data-id="element-103" />
                <span className="text-xs font-mono text-[#9A9A9A] truncate" data-id="element-104">
                  Tracking {ns.trackingId}
                </span>
              </div>
              <div className="text-xl font-semibold text-[#FAFAFA] font-heading font-mono" data-id="element-105">
                {ns.id}
              </div>
              <div className="text-sm text-[#9A9A9A] mt-0.5 flex items-center gap-1.5" data-id="element-106">
                <MapPin className="h-3.5 w-3.5 shrink-0" data-id="element-107" />
                <span className="truncate" data-id="element-108">{ns.destination}</span>
              </div>
            </div>
          </div>
          <div className="flex items-stretch gap-5 sm:gap-8 shrink-0" data-id="element-109">
            <div data-id="element-110">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-111">
                Stato
              </div>
              <div className="mt-1.5" data-id="element-112">
                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-[#5DA9FF]/10 text-[#5DA9FF] border border-[#5DA9FF]/30" data-id="element-113">
                  {ns.statusLabel}
                </span>
              </div>
            </div>
            <div className="border-l border-[#262626] pl-5 sm:pl-8" data-id="element-114">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium flex items-center gap-1" data-id="element-115">
                <CalendarCheck className="h-3 w-3" data-id="element-116" />
                Consegna prevista
              </div>
              <div className="text-sm font-mono font-semibold text-[#FAFAFA] mt-1.5" data-id="element-117">
                {ns.eta}
              </div>
            </div>
          </div>
        </div>

        {/* Progress section — matches admin Spedizioni detail */}
        <div className="border-t border-[#262626] bg-[#0F0F0F] px-4 sm:px-5 py-4" data-id="element-118">
          <div className="flex items-end justify-between mb-2.5" data-id="element-119">
            <div data-id="element-120">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-0.5" data-id="element-121">
                Spedito
              </div>
              <div className="font-mono text-sm text-[#9A9A9A]" data-id="element-122">
                {ns.departure}
              </div>
            </div>
            <div className="text-right" data-id="element-123">
              <div className="text-[10px] uppercase tracking-wider text-[#D4FF3A]/80 font-semibold mb-0.5 flex items-center justify-end gap-1" data-id="element-124">
                <CalendarCheck className="h-3.5 w-3.5 text-[#D4FF3A]" data-id="element-125" />
                Consegna prevista
              </div>
              <div className="font-mono text-base sm:text-lg font-bold text-[#FAFAFA] flex items-center justify-end gap-1.5" data-id="element-126">
                {ns.eta}
                {ns.anomaly && <AlertTriangle className="h-4 w-4 text-[#EF4444] shrink-0" aria-label="Anomalia rilevata" data-id="element-127" />}
              </div>
            </div>
          </div>
          <div className="relative h-2 w-full bg-[#0A0A0A] border border-[#262626] rounded-full mb-2" data-id="element-128">
            <div className={`absolute top-0 left-0 h-full rounded-full ${ns.anomaly ? 'bg-[#EF4444]' : 'bg-[#D4FF3A]'}`} style={{
            width: `${ns.progress}%`
          }} data-id="element-129" />
            <div className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-3 w-3 rounded-full ring-2 ring-[#0F0F0F] ${ns.anomaly ? 'bg-[#EF4444]' : 'bg-[#D4FF3A]'}`} style={{
            left: `${ns.progress}%`
          }} data-id="element-130" />
          </div>
          {ns.anomaly ? <button type="button" className="w-full mt-1 flex items-center justify-center gap-1.5 text-[11px] text-[#EF4444] hover:text-[#FAFAFA] transition group" data-id="element-131">
              <AlertTriangle className="h-3 w-3 shrink-0" data-id="element-132" />
              <span className="truncate" data-id="element-133">{ns.anomaly.summary}</span>
              <span className="inline-flex items-center gap-0.5 font-medium text-[#FAFAFA] opacity-80 group-hover:opacity-100 shrink-0" data-id="element-134">
                · Risolvi
                <ChevronRight className="h-3 w-3" data-id="element-135" />
              </span>
            </button> : <div className="text-[11px] text-[#6B6B6B] text-center" data-id="element-136">
              {ns.completedSteps} di {ns.totalSteps} fasi completate
            </div>}
        </div>

        {/* Current phase */}
        <div className="border-t border-[#262626] bg-[#0F0F0F] px-4 sm:px-5 py-3 flex items-center gap-3" data-id="element-137">
          <span className="relative flex h-2.5 w-2.5 shrink-0" data-id="element-138">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#D4FF3A] opacity-60 animate-ping" data-id="element-139" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#D4FF3A]" data-id="element-140" />
          </span>
          <div className="min-w-0" data-id="element-141">
            <div className="text-[10px] uppercase tracking-wider text-[#D4FF3A]/80 font-semibold" data-id="element-142">
              Fase attuale
            </div>
            <div className="text-sm font-medium text-[#FAFAFA] truncate" data-id="element-143">
              {ns.currentPhase}
            </div>
          </div>
        </div>
      </Widget>

      {/* Two lists at 50/50 */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-144">
        {/* Spedizioni recenti */}
        <Widget title="Spedizioni recenti" footerCta={{
        label: 'Vedi tutte'
      }} bodyPadding="none" data-id="element-145">
          <ul className="divide-y divide-[#1F1F1F]" data-id="element-146">
            {RECENT_SHIPMENTS.map(s => <li key={s.id} data-id="element-147">
                <button type="button" onClick={() => onOpenShipment?.(s.id)} className="w-full text-left px-5 py-3.5 flex items-center justify-between gap-3 hover:bg-[#0A0A0A] transition-colors" data-id="element-148">
                  <div className="flex items-center gap-3 min-w-0" data-id="element-149">
                    <div className="h-7 w-9 rounded bg-white flex items-center justify-center shrink-0 p-1" data-id="element-150">
                      <img src={CARRIER_LOGOS[s.carrier]} alt={s.carrier} className="max-w-full max-h-full object-contain" data-id="element-151" />
                    </div>
                    <div className="min-w-0" data-id="element-152">
                      <div className="text-sm font-medium text-[#FAFAFA] truncate" data-id="element-153">
                        {s.name}
                      </div>
                      <div className="text-[11px] text-[#6B6B6B] font-mono mt-0.5" data-id="element-154">
                        {s.id} · {s.date} · {s.colli} colli
                      </div>
                    </div>
                  </div>
                  <span className={`text-xs ${s.statusColor} shrink-0`} data-id="element-155">
                    {s.status}
                  </span>
                </button>
              </li>)}
          </ul>
        </Widget>

        {/* Ticket recenti */}
        <Widget title="Ticket recenti" footerCta={{
        label: 'Vedi tutti',
        onClick: () => onOpenAllTickets?.()
      }} bodyPadding="none" data-id="element-156">
          {RECENT_TICKETS.length === 0 ? <div className="p-8 text-center text-sm text-[#6B6B6B]" data-id="element-157">
              Nessun ticket aperto.
            </div> : <ul className="divide-y divide-[#1F1F1F]" data-id="element-158">
              {RECENT_TICKETS.map(t => {
            const cfg = TICKET_STATUS_STYLES[t.status];
            const Icon = cfg.icon;
            return <li key={t.id} data-id="element-159">
                    <button type="button" onClick={() => onOpenTicket?.(t.id)} className="w-full text-left px-5 py-3.5 flex items-center justify-between gap-3 hover:bg-[#0A0A0A] transition-colors" data-id="element-160">
                      <div className="flex items-center gap-2.5 min-w-0" data-id="element-161">
                        <MessageSquare className="h-3.5 w-3.5 text-[#6B6B6B] shrink-0" data-id="element-162" />
                        <div className="min-w-0" data-id="element-163">
                          <div className="text-sm text-[#FAFAFA] truncate" data-id="element-164">
                            {t.subject}
                          </div>
                          <div className="text-[11px] text-[#6B6B6B] font-mono mt-0.5" data-id="element-165">
                            {t.id} · agg. {t.updated}
                          </div>
                        </div>
                      </div>
                      <span className={`inline-flex items-center gap-1 text-xs ${cfg.text} shrink-0`} data-id="element-166">
                        <Icon className="h-3 w-3" data-id="element-167" />
                        {t.status}
                      </span>
                    </button>
                  </li>;
          })}
            </ul>}
        </Widget>
      </section>
    </div>;
}